import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { useEapiAuth } from "@/hooks/useEapiAuth";
import { toast } from "sonner";
import { uploadTrailerEventPhoto, uploadBolPhoto } from "@/components/yard/yardPhotoUtils";
import {
  checkInTrailer,
  checkOutTrailer,
  recordTrailerCheckInOut,
  type CheckInMetadata,
} from "@/lib/trailer-yard-ops";
import { queryKeys } from "@/lib/query-keys";
import { progressLeg, describeYardCheckInResult } from "@/lib/leg-progression";
import type { OutboundAymsEventResult } from "@/lib/ayms-event";
import { syncTrailerBol } from "@/lib/trailer-bol-sync";

export type YardDirection = "in" | "out";

const directionConfig = {
  in: {
    trailerStatus: "at_site" as const,
    orderStatus: "delivered" as const,
    label: "Check In",
    toastVerb: "checked in",
  },
  out: {
    trailerStatus: "in_transit" as const,
    orderStatus: "in_transit" as const,
    label: "Check Out",
    toastVerb: "checked out",
  },
};

interface UseYardActionParams {
  locationId: string;
  locationCity: string;
  direction: YardDirection;
  pendingPhoto: File | null;
  /**
   * Check-in only: right-side trailer photo. When provided, the primary
   * `pendingPhoto` is uploaded as the left-side variant and this file as
   * the right-side variant. Both still use `photo_type = "trailer"`.
   */
  pendingPhotoRight?: File | null;
  /** Check-in only: seal photo (required when the trailer is loaded). */
  pendingSealPhoto?: File | null;
  /** Check-in only: license plate photo (always required at manual check-in). */
  pendingLicensePlatePhoto?: File | null;
  pendingBolPhoto?: File | null;
  onPhotoUploaded: () => void;
  onPhotoRightUploaded?: () => void;
  onSealPhotoUploaded?: () => void;
  onLicensePlatePhotoUploaded?: () => void;
  onBolPhotoUploaded?: () => void;
}

export function useYardAction({
  locationId,
  locationCity,
  direction,
  pendingPhoto,
  pendingPhotoRight,
  pendingSealPhoto,
  pendingLicensePlatePhoto,
  pendingBolPhoto,
  onPhotoUploaded,
  onPhotoRightUploaded,
  onSealPhotoUploaded,
  onLicensePlatePhotoUploaded,
  onBolPhotoUploaded,
}: UseYardActionParams) {
  const { user } = useAuth();
  const { activeTenant, tenantId } = useTenant();
  const { eapiClient } = useEapiAuth();
  const queryClient = useQueryClient();
  const [isProcessing, setProcessingRaw] = useState(false);
  /** Human-readable step shown while the yard action runs (photo uploads etc.). */
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  const setIsProcessing = (value: boolean) => {
    setProcessingRaw(value);
    if (!value) setProgressLabel(null);
  };
  const cfg = directionConfig[direction];

  const invalidateAll = () => {
    setProgressLabel("Refreshing…");
    queryClient.invalidateQueries({ queryKey: queryKeys.YARD_CHECKOUT_ORDERS });
    queryClient.invalidateQueries({ queryKey: queryKeys.YARD_CHECKIN_ORDERS });
    queryClient.invalidateQueries({ queryKey: queryKeys.TRAILERS });
    queryClient.invalidateQueries({ queryKey: queryKeys.ORDERS });
    // YardSiteView uses its own keys that don't share a prefix with
    // queryKeys.TRAILERS; without these, the on-site trailer list stays
    // stale after checkout and the just-checked-out trailer keeps
    // showing up, letting the user check it out twice.
    queryClient.invalidateQueries({ queryKey: ["trailers-on-site"] });
    queryClient.invalidateQueries({ queryKey: ["yard-site-trailer-count"] });
  };

  /**
   * Upload all pending trailer photos captured on the yard form and link
   * each to the freshly-created trailer_event. Any single-photo failure is
   * surfaced as a soft toast but does not roll back the check-in.
   */
  const uploadPhotosForEvent = async (trailerDbId: string, eventId: string) => {
    if (!eventId) return;
    const uploads: Array<{
      file: File;
      type: "trailer" | "seal" | "license_plate";
      pathVariant?: string;
      onDone?: () => void;
      label: string;
    }> = [];
    // When both a left and right trailer photo are present, tag their
    // storage paths so the readiness checklist can count each side
    // independently without adding new enum values.
    const splitTrailerPhotos = !!(pendingPhoto && pendingPhotoRight);
    if (pendingPhoto) {
      uploads.push({
        file: pendingPhoto,
        type: "trailer",
        pathVariant: splitTrailerPhotos ? "trailer-left" : undefined,
        onDone: onPhotoUploaded,
        label: splitTrailerPhotos ? "Trailer photo (left)" : "Trailer photo",
      });
    }
    if (pendingPhotoRight) {
      uploads.push({
        file: pendingPhotoRight,
        type: "trailer",
        pathVariant: "trailer-right",
        onDone: onPhotoRightUploaded,
        label: "Trailer photo (right)",
      });
    }
    if (pendingSealPhoto) uploads.push({ file: pendingSealPhoto, type: "seal", onDone: onSealPhotoUploaded, label: "Seal photo" });
    if (pendingLicensePlatePhoto) uploads.push({ file: pendingLicensePlatePhoto, type: "license_plate", onDone: onLicensePlatePhotoUploaded, label: "License plate photo" });
    for (const [i, u] of uploads.entries()) {
      setProgressLabel(
        uploads.length > 1
          ? `Uploading photo ${i + 1} of ${uploads.length}…`
          : `Uploading ${u.label.toLowerCase()}…`,
      );
      try {
        await uploadTrailerEventPhoto({
          eventId,
          trailerDbId,
          file: u.file,
          photoType: u.type,
          pathVariant: u.pathVariant,
          tenantId,
          userId: user?.id,
        });
        u.onDone?.();
      } catch (err) {
        console.error(`${u.label} upload failed`, err);
        toast.warning(`${u.label} failed to upload`);
      }
    }
  };

  const uploadBolIfPresent = async (eventId: string) => {
    if (pendingBolPhoto && eventId) {
      setProgressLabel("Uploading BOL photo…");
      await uploadBolPhoto(eventId, pendingBolPhoto, user?.id, user?.email ?? undefined, tenantId ?? undefined);
      onBolPhotoUploaded?.();
    }
  };

  /**
   * Yard check-in completes the matching leg, then defers parent promotion
   * decisions to the shared `progressLeg` orchestrator (same code path as the
   * driver hook). The yard does NOT fire trailer events from here — the
   * `checkInTrailer` / `checkOutTrailer` helpers in `trailer-yard-ops` own
   * that side effect on the yard side.
   */
  const completeLegAndMaybePromoteParent = (
    parentOrderId: string,
    legId: string | null,
    actorLabel: string,
  ) =>
    progressLeg({
      parentOrderId,
      legId,
      newStatus: "delivered",
      actorLabel,
      tenantId,
      // Yard staff (terminal_staff) cannot UPDATE legs/orders directly under
      // RLS; the definer RPC performs the transition after checking the
      // caller's role for the order's own tenant.
      viaYardRpc: true,
    });

  const actorLabel = user?.email || "Yard";

  /**
   * The outbound AYMS event is sent with the trailer event (recordTrailerCheckInOut). It
   * never blocks the yard action; a failed submit only surfaces a soft toast.
   */
  const warnIfOutboundAymsEventFailed = (outboundAymsEvent: Promise<OutboundAymsEventResult> | null | undefined) => {
    void outboundAymsEvent?.then((r) => {
      if (!r.ok && r.reason === "threw") toast.warning("Yard event not synced to EAPI");
    });
  };

  /**
   * Check a trailer in or out against one order's leg. Check-in takes the
   * same CheckInMetadata manual check-in submits (from the overlay's
   * CheckInForm); the leg's own load state is only a fallback.
   */
  const processOrderAction = async (order: any, meta?: CheckInMetadata) => {
    setIsProcessing(true);
    setProgressLabel(direction === "in" ? "Saving check-in…" : "Saving check-out…");
    await supabase.rpc("set_tenant_slug", { _slug: activeTenant });
    // The order's trailer has no row in this tenant: the order still moves,
    // but there is no trailer event to write and so no AYMS event to send.
    const warnTrailerNotFound = () =>
      console.warn("[yard] order's trailer not found in tenant; no trailer event or AYMS event sent", {
        trailerHumanId: order.trailer_id,
        orderId: order.id,
        direction,
        tenantId,
        locationId,
      });
    try {
      if (direction === "in") {
        // Carry the leg's known load state through so the check-in doesn't
        // wipe the trailer's load status. When unknown, pass nothing and the
        // helper preserves the existing value.
        const legLoadStatus =
          order?.loadStatus === "loaded" || order?.loadStatus === "empty"
            ? order.loadStatus
            : order?.is_loaded === true
              ? "loaded"
              : order?.is_loaded === false
                ? "empty"
                : undefined;
        const checkInMeta: CheckInMetadata = { ...meta, loadStatus: meta?.loadStatus || legLoadStatus };
        const result = await checkInTrailer(order.trailer_id, locationId, user?.id, checkInMeta, tenantId ?? undefined, {
          eapiClient,
          orderId: order.id,
          tractorId: meta?.truckId,
        });
        warnIfOutboundAymsEventFailed(result?.outboundAymsEvent);
        if (result) {
          await uploadPhotosForEvent(result.trailer.id, result.trailerEventId);
          await uploadBolIfPresent(result.trailerEventId);
          // Reverse-direction sync, as manual check-in: carry-forward when no
          // fresh BoL was captured (and the trailer isn't empty), then fan out
          // to any orders already assigned to this trailer.
          await syncTrailerBol({
            direction: "trailer-gains-bol",
            trailerHumanId: order.trailer_id,
            tenantId,
            tenantSlug: activeTenant,
            carryForward:
              !pendingBolPhoto && checkInMeta.loadStatus !== "empty"
                ? { trailerDbId: result.trailer.id, newEventId: result.trailerEventId }
                : undefined,
          });
        } else {
          warnTrailerNotFound();
        }
      } else {
        const result = await checkOutTrailer(
          order.trailer_id,
          locationId,
          user?.id,
          tenantId ?? undefined,
          { sealStatus: meta?.sealStatus },
          { eapiClient, orderId: order.id, tractorId: meta?.truckId },
        );
        warnIfOutboundAymsEventFailed(result?.outboundAymsEvent);
        if (result) await uploadPhotosForEvent(result.trailer.id, result.trailerEventId);
        else warnTrailerNotFound();
      }

      if (direction === "in") {
        const progress = await completeLegAndMaybePromoteParent(order.id, order.legId ?? null, actorLabel);
        toast.success(describeYardCheckInResult(order.trailer_id, progress));
        invalidateAll();
        return;
      } else {
        await supabase.from("orders").update({ status: cfg.orderStatus }).eq("id", order.id);
      }

      toast.success(`Trailer ${order.trailer_id} ${cfg.toastVerb}`);
      invalidateAll();
    } catch (err) {
      toast.error(`Failed to ${cfg.label.toLowerCase()} trailer`);
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const processManualAction = async (trailerId: string, meta?: CheckInMetadata): Promise<boolean> => {
    setIsProcessing(true);
    setProgressLabel(direction === "in" ? "Saving check-in…" : "Saving check-out…");
    let lastProgress: Awaited<ReturnType<typeof completeLegAndMaybePromoteParent>> | null = null;
    await supabase.rpc("set_tenant_slug", { _slug: activeTenant });
    try {
      if (direction === "in") {
        // Leg-aware: find legs whose ship_to_city matches this yard for any
        // active parent order with this trailer. Looked up before the
        // check-in so the outbound AYMS event can name the order it delivers; the
        // legs are completed after it.
        const { data: parentOrders } = await supabase
          .from("orders")
          .select("id")
          .eq("trailer_id", trailerId)
          .in("status", ["confirmed", "in_transit"]);
        const parentIds = (parentOrders || []).map((o: any) => o.id);
        const matchingLegs = parentIds.length
          ? (
              await supabase
                .from("legs")
                .select("id, order_id")
                .in("order_id", parentIds)
                .eq("ship_to_city", locationCity)
                .or("driver_status.is.null,driver_status.neq.delivered")
            ).data ?? []
          : [];

        const result = await checkInTrailer(trailerId, locationId, user?.id, meta, tenantId ?? undefined, {
          eapiClient,
          orderId: matchingLegs.at(-1)?.order_id ?? null,
          tractorId: meta?.truckId,
        });
        warnIfOutboundAymsEventFailed(result?.outboundAymsEvent);
        if (!result) {
          setIsProcessing(false);
          return false;
        }
        await uploadPhotosForEvent(result.trailer.id, result.trailerEventId);
        await uploadBolIfPresent(result.trailerEventId);
        const loadStatus = meta?.loadStatus;
        // Reverse-direction sync: carry-forward when no fresh BoL was
        // captured (and the trailer isn't empty), then fan out to any
        // orders already assigned to this trailer.
        await syncTrailerBol({
          direction: "trailer-gains-bol",
          trailerHumanId: trailerId,
          tenantId,
          tenantSlug: activeTenant,
          carryForward:
            !pendingBolPhoto && loadStatus !== "empty"
              ? { trailerDbId: result.trailer.id, newEventId: result.trailerEventId }
              : undefined,
        });

        // Complete the matching legs one at a time. Parent promotion happens
        // inside the helper only when all sibling legs are delivered.
        for (const leg of matchingLegs) {
          lastProgress = await completeLegAndMaybePromoteParent(leg.order_id, leg.id, actorLabel);
        }
      } else {
        const result = await checkOutTrailer(
          trailerId,
          locationId,
          user?.id,
          tenantId ?? undefined,
          { sealStatus: meta?.sealStatus },
          { eapiClient, orderId: null, tractorId: meta?.truckId },
        );
        warnIfOutboundAymsEventFailed(result?.outboundAymsEvent);
        if (!result) {
          setIsProcessing(false);
          return false;
        }

        // For manual check-out, set status to "unknown"
        await supabase.from("trailers")
          .update({ current_location_status: "unknown" as const, current_location_id: null, seal_status: meta?.sealStatus || null })
          .eq("id", result.trailer.id);

        await uploadPhotosForEvent(result.trailer.id, result.trailerEventId);
      }

      toast.success(
        lastProgress ? describeYardCheckInResult(trailerId, lastProgress) : `Trailer ${trailerId} ${cfg.toastVerb}`,
      );
      invalidateAll();
      return true;
    } catch (err) {
      toast.error(`Failed to ${cfg.label.toLowerCase()} trailer`);
      console.error(err);
      return true;
    } finally {
      setIsProcessing(false);
    }
  };

  const createAndProcess = async (trailerId: string, trailerTypeId?: string | null, meta?: CheckInMetadata) => {
    setIsProcessing(true);
    setProgressLabel("Creating trailer…");
    try {
      const newStatus = direction === "in" ? "at_site" as const : "unknown" as const;
      const newLocationId = direction === "in" ? locationId : null;

      const trailerPayload: any = {
          trailer_id: trailerId,
          trailer_type_id: trailerTypeId || null,
          current_location_status: newStatus,
          current_location_id: newLocationId,
          seal_id: meta?.sealId || null,
          load_status: meta?.loadStatus || null,
          seal_status: meta?.sealStatus || null,
          scac: meta?.scac || null,
          is_customer_parking: meta?.isCustomerParking ?? false,
          needs_service: meta?.needsService ?? false,
          needs_service_reason: meta?.needsService ? (meta?.needsServiceReason?.trim() || null) : null,
          needs_service_set_at: meta?.needsService ? new Date().toISOString() : null,
          needs_service_set_by: meta?.needsService ? (user?.id ?? null) : null,
        };
      if (tenantId) trailerPayload.tenant_id = tenantId;

      const { data: newTrailer, error: createErr } = await supabase
        .from("trailers")
        .insert(trailerPayload as any)
        .select("id")
        .single();
      if (createErr) throw createErr;

      // A brand-new trailer has no order history; its outbound AYMS event is still
      // reported to AYMS, with no load (ASE-20496).
      const trailerEvent = await recordTrailerCheckInOut({
        trailerDbId: newTrailer.id,
        trailerHumanId: trailerId,
        direction,
        locationId,
        tenantId,
        userId: user?.id,
        details: {
          truck_id: meta?.truckId || null,
          seal_id: meta?.sealId || null,
          load_status: meta?.loadStatus || null,
          seal_status: meta?.sealStatus || null,
          notes: meta?.isFoundTrailer ? "Found during yard audit" : (meta?.notes || null),
          needs_service: meta?.needsService ?? false,
          needs_service_reason: meta?.needsService ? (meta?.needsServiceReason?.trim() || null) : null,
        },
        outboundAymsEventContext: { eapiClient, orderId: null, tractorId: meta?.truckId },
      });
      warnIfOutboundAymsEventFailed(trailerEvent.outboundAymsEvent);

      if (trailerEvent.trailerEventId) await uploadPhotosForEvent(newTrailer.id, trailerEvent.trailerEventId);
      if (trailerEvent.trailerEventId) await uploadBolIfPresent(trailerEvent.trailerEventId);

      // New-trailer path: if any pre-existing order already targets this
      // trailer_id (unusual but possible when the trailer was referenced
      // before it existed as a row), fan out the just-captured BoL.
      if (direction === "in" && trailerEvent.trailerEventId) {
        await syncTrailerBol({
          direction: "trailer-gains-bol",
          trailerHumanId: trailerId,
          tenantId,
          tenantSlug: activeTenant,
        });
      }

      toast.success(`New trailer ${trailerId} created and ${cfg.toastVerb}`);
      invalidateAll();
    } catch (err) {
      toast.error(`Failed to create and ${cfg.label.toLowerCase()} trailer`);
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return { isProcessing, progressLabel, processOrderAction, processManualAction, createAndProcess };
}
